import { API, graphqlOperation } from 'aws-amplify';
import type Dexie from 'dexie';

import { type CreateLegInput } from '../../API';
import { collectDocumentEdits, mergeDocumentEdits } from '../../components/PhotoCapture/photoCaptureUtils';
import { isSome } from '../../utils';
import { getAircraft } from '../Aircrafts/AircraftsDatasource';
import { type Aircraft } from '../Aircrafts/types';
import { getCountableChangesForLeg, getLastCountableChangeForLeg } from '../CountableChanges/CountableChangeDatasource';
import { type CountableChange } from '../CountableChanges/types';
import { COUNTABLE_CHANGE_UPLIFT } from '../CountableChanges/types';
import { sanitizeId } from '../datasourceUtils';
import { type FlightDocument } from '../FlightDocuments/FlightDocument';
import { getFlightDocumentsForLeg } from '../FlightDocuments/FlightDocumentsDatasource';
import { getDbSyncer } from '../LocalDB/DbSyncer';
import { getDb } from '../LocalDB/LocalDB';
import { flightLegFromDb, flightLegItemFields } from '../LocalDB/Transformers/flightLegs';
import { getSquawkDeferralsForSquawk } from '../Squawks/getSquawkDeferralsForSquawk';
import { getSquawksForLeg } from '../Squawks/getSquawksForLeg';
import { type Squawk, type SquawkDeferral } from '../Squawks/types';
import { type FlightLeg, type FlightLegPhase, type WithLastCountableChange } from './FlightLeg';
import { compareLegsInFlight, FLIGHT_LEG_PHASE_FINISH } from './FlightLeg';

export const saveFlightLeg = async (
    leg: FlightLeg,
    phase: FlightLegPhase,
    countableChange: CountableChange,
    documents: FlightDocument[],
    squawks?: Squawk[],
    squawkDeferrals?: SquawkDeferral[]
): Promise<void> => {
    const db = getDb();
    await db.transaction(
        'rw',
        [db.legs, db.countableChanges, db.documents, db.squawks, db.squawkDeferrals, db.syncItems],
        async () => {
            const [legDocuments, existingSquawks, existingCountableChanges] = await Promise.all([
                getFlightDocumentsForLeg(leg.id, phase),
                getSquawksForLeg(leg.id),
                getCountableChangesForLeg(leg.id),
            ]);
            // Squawk documents aren't among the leg's documents above; without their stored rows they would be put
            // over the stored ones as the form holds them, losing contentKey and _version.
            const legDocumentIds = new Set(legDocuments.map((document) => document.id));
            const otherSubmittedIds = documents.map((document) => document.id).filter((id) => !legDocumentIds.has(id));
            const otherStoredDocuments = (await db.documents.bulkGet(otherSubmittedIds)).filter(isSome);
            const existingDocuments = [...legDocuments, ...otherStoredDocuments];

            const documentIntersection = intersectItems(existingDocuments, documents);
            // the server has it but this device no longer does: deleted elsewhere, whose download removed the row
            documentIntersection.newOnly = documentIntersection.newOnly.filter(
                (document) => document._version === undefined
            );
            // documents can not be updated, so no need to PUT them
            const promises: Array<Promise<any>> = [];
            const onlyLocalDocumentIds = getOnlyLocalIds(documentIntersection);
            if (onlyLocalDocumentIds.length) {
                promises.push(db.documents.where('id').anyOf(onlyLocalDocumentIds).delete());
                promises.push(db.syncItems.bulkDelete(getSyncItemIds(db.documents, onlyLocalDocumentIds)));
            }
            if (documentIntersection.newOnly.length) {
                promises.push(db.documents.bulkPut(documentIntersection.newOnly));
            }
            const documentEdits = collectDocumentEdits(existingDocuments, documents);
            if (documentEdits.length) {
                promises.push(db.documents.bulkPut(mergeDocumentEdits(existingDocuments, documentEdits)));
            }
            let squawkIntersection = null;
            let squawkDeferralIntersection = null;
            if (squawks !== undefined) {
                squawkIntersection = intersectItems(existingSquawks, squawks);
                const squawksToPut = [...squawkIntersection.newOnly, ...squawkIntersection.both];
                const onlyLocalSquawkIds = getOnlyLocalIds(squawkIntersection);
                if (onlyLocalSquawkIds.length) {
                    promises.push(db.squawks.where('id').anyOf(onlyLocalSquawkIds).delete());
                    promises.push(db.syncItems.bulkDelete(getSyncItemIds(db.squawks, onlyLocalSquawkIds)));
                }
                if (squawksToPut.length) {
                    promises.push(db.squawks.bulkPut(squawksToPut));
                }
                if (squawkDeferrals !== undefined) {
                    const existingSquawkDeferrals = (
                        await Promise.all(squawks.map(async (s) => getSquawkDeferralsForSquawk(s.id)))
                    ).flat();
                    squawkDeferralIntersection = intersectItems(existingSquawkDeferrals, squawkDeferrals);
                    const squawksDeferralsToPut = [
                        ...squawkDeferralIntersection.newOnly,
                        ...squawkDeferralIntersection.both,
                    ];
                    const onlyLocalSquawkDeferralIds = getOnlyLocalIds(squawkDeferralIntersection);
                    if (onlyLocalSquawkDeferralIds.length) {
                        promises.push(db.squawkDeferrals.where('id').anyOf(onlyLocalSquawkDeferralIds).delete());
                        promises.push(
                            db.syncItems.bulkDelete(getSyncItemIds(db.squawkDeferrals, onlyLocalSquawkDeferralIds))
                        );
                    }
                    if (squawksDeferralsToPut.length) {
                        promises.push(db.squawkDeferrals.bulkPut(squawksDeferralsToPut));
                    }
                }
            }
            let upliftToSync: string | null = null;
            if (phase === FLIGHT_LEG_PHASE_FINISH) {
                const uplift = existingCountableChanges.find((cc) => cc.type === COUNTABLE_CHANGE_UPLIFT);
                if (uplift) {
                    const valuesDiffer = uplift.countableValuesAfter.some(
                        (value, index) => value !== countableChange.countableValuesBefore[index]
                    );
                    const typesAreSame = uplift.countableTypes.join('-') === countableChange.countableTypes.join('-');
                    if (valuesDiffer && typesAreSame) {
                        uplift.countableValuesAfter = [...countableChange.countableValuesBefore];
                        promises.push(db.countableChanges.put(uplift));
                        upliftToSync = uplift.id;
                    }
                }
            }

            promises.push(db.countableChanges.put(countableChange));
            promises.push(db.legs.put(leg));
            await Promise.all(promises);
            const dbSyncer = getDbSyncer();
            dbSyncer.markToSync(db.legs, leg.id);
            dbSyncer.markToSync(db.documents, ...getSubmittedIds(documentIntersection));
            if (squawkIntersection !== null) {
                dbSyncer.markToSync(db.squawks, ...getSubmittedIds(squawkIntersection));
            }
            if (squawkDeferralIntersection !== null) {
                dbSyncer.markToSync(db.squawkDeferrals, ...getSubmittedIds(squawkDeferralIntersection));
            }
            dbSyncer.markToSync(db.countableChanges, countableChange.id);
            if (upliftToSync !== null) {
                dbSyncer.markToSync(db.countableChanges, upliftToSync);
            }
        }
    );
};

const toIdSet = (input: Array<{ id: string }>): Set<string> => new Set<string>(input.map((i) => i.id));

type Intersection<T extends { id: string }> = {
    oldOnly: T[];
    newOnly: T[];
    both: T[];
};
const intersectItems = <T extends { id: string }>(oldItems: T[], newItems: T[]): Intersection<T> => {
    const oldOnly: T[] = [];
    const newOnly: T[] = [];
    const both: T[] = [];
    const oldIds = toIdSet(oldItems);
    const newIds = toIdSet(newItems);
    for (const item of oldItems) {
        if (!newIds.has(item.id)) {
            oldOnly.push(item);
        }
    }
    for (const item of newItems) {
        if (oldIds.has(item.id)) {
            both.push(item);
        } else {
            newOnly.push(item);
        }
    }

    return { oldOnly, newOnly, both };
};

const getSubmittedIds = <T extends { id: string }>(intersection: Intersection<T>): string[] => [
    ...intersection.newOnly.map((i) => i.id),
    ...intersection.both.map((i) => i.id),
];

// A stored record missing from the submitted form is one the form never loaded (it can open from a
// draft older than a sync): only a never-synced one goes, a synced one stays as it is. A synced
// record is deleted only by submitting it flagged deleted.
const getOnlyLocalIds = <T extends { id: string; _version?: number | null }>(intersection: Intersection<T>): string[] =>
    intersection.oldOnly.filter((item) => item._version === undefined).map((item) => item.id);

// The sync item must go with the row: syncItemToCommand throws on a missing row.
const getSyncItemIds = (table: Dexie.Table, ids: string[]): string[] => ids.map((id) => `${table.name}:${id}`);

export const getFlightLegsForFlight = async (flightId: string): Promise<Array<FlightLeg & WithLastCountableChange>> => {
    const legs = (await getDb().legs.where('flightId').equals(flightId).toArray()).filter((leg) => !leg.deleted);
    legs.sort(compareLegsInFlight);
    const lastCountables = await Promise.all(legs.map(getLastCountableChangeForLeg));

    return legs.map((leg, index) => ({
        ...leg,
        lastCountableChange: lastCountables[index],
    }));
};

export type GetFlightLegParams = [string];

export const getFlightLeg = async (legId: GetFlightLegParams[0]): Promise<FlightLeg | null> => {
    const maybeLeg = await getDb().legs.get(legId);

    return maybeLeg || null;
};

export const getFlightLegsById = async (legIds: Array<GetFlightLegParams[0]>): Promise<Array<FlightLeg | null>> =>
    (await getDb().legs.bulkGet(legIds)).map((l) => l ?? null);

export type FlightLegAndAircraft = { leg: FlightLeg; aircraft: Aircraft };

export const getFlightLegAndAircraft = async (legId: GetFlightLegParams[0]): Promise<FlightLegAndAircraft | null> => {
    const leg = await getFlightLeg(legId);
    if (!leg) {
        return null;
    }
    const aircraft = await getAircraft(leg.aircraftId);
    if (!aircraft) {
        return null;
    }

    return { leg, aircraft };
};

export const deleteFlightLeg = async (legId: string): Promise<void> => {
    const db = getDb();
    await db.transaction('rw', [db.legs, db.countableChanges, db.documents, db.squawks, db.syncItems], async () => {
        const leg = await db.legs.get(legId);
        if (!leg) {
            return;
        }
        const [existingDocuments, existingCountableChanges, existingSquawks] = await Promise.all([
            db.documents.where({ legId }).toArray(),
            db.countableChanges.where({ legId }).toArray(),
            db.squawks.where({ legId }).toArray(),
        ]);
        leg.deleted = true;
        existingDocuments.forEach((doc) => (doc.deleted = true));
        existingCountableChanges.forEach((cc) => (cc.deleted = true));
        existingSquawks.forEach((s) => (s.deleted = true));

        await Promise.all([
            db.legs.put(leg),
            db.countableChanges.bulkPut(existingCountableChanges),
            db.squawks.bulkPut(existingSquawks),
            db.documents.bulkPut(existingDocuments),
        ]);
        const dbSyncer = getDbSyncer();
        dbSyncer.markToSync(db.documents, ...existingDocuments.map((d) => d.id));
        dbSyncer.markToSync(db.squawks, ...existingSquawks.map((s) => s.id));
        dbSyncer.markToSync(db.countableChanges, ...existingCountableChanges.map((cc) => cc.id));
        dbSyncer.markToSync(db.legs, legId);
    });
};

export const getCrewFlightLegsOnline = async (crewId: string): Promise<FlightLeg[]> => {
    const legsQuery = `query ListLegs($crewId: ID) {
         legsByPicId(picId: $crewId, limit: 400, sortDirection: DESC) {
            items {
                ${flightLegItemFields}
            }
        }
        legsBySicId(sicId: $crewId, limit: 400, sortDirection: DESC) {
            items {
                ${flightLegItemFields}
            }
        }
    }`;
    const gqlo = graphqlOperation(legsQuery, { crewId });
    const legsResult = (await API.graphql(gqlo)) as {
        data: { legsByPicId: { items: CreateLegInput[] }; legsBySicId: { items: CreateLegInput[] } };
    };
    const legInputs = [...legsResult.data.legsByPicId.items, ...legsResult.data.legsBySicId.items];

    return legInputs.map(flightLegFromDb);
};

export const getFlightLegsByIdOnline = async (ids: Array<GetFlightLegParams[0]>): Promise<FlightLeg[]> => {
    if (!ids.length) {
        return [];
    }

    const queryItems = ids.map(
        (id, index) => `
    getLeg${index}: getLeg(id: "${sanitizeId(id)}") {
        ${flightLegItemFields}
    }`
    );
    const query = `
        query GetFlightLegs {
            ${queryItems.join('\n')}
        }
    `;

    const gqlo = graphqlOperation(query);
    const legsResult = (await API.graphql(gqlo)) as { data: { [key: string]: CreateLegInput } };
    const legInputs = Object.values(legsResult.data);
    console.log(legInputs);

    return legInputs.map(flightLegFromDb);
};
